<template>
  <!-- SEARCH INPUT -->
  <Container indent="wide" class="mb-5 max-w-5xl xl:mx-auto xl:w-3/4">
    <SearchInput
      class="-mt-1 w-full pb-4 sm:-mt-0 sm:py-4"
      auto-focus
      :model-value="route.query.search"
      @update:model-value="search($event)"
    />
  </Container>

  <!-- RESULTS -->
  <Container indent="wide">
    <div
      v-show="params['search']"
      class="grid grid-cols-1 gap-12 xl:grid-cols-2"
    >
      <!-- DOCUMENTS -->
      <div class="order-2 xl:order-1">
        <h2 class="mb-6 text-2xl font-medium sm:mb-8 sm:text-3xl">
          {{ t('search.documents_found', results.documents.nbHits || 0) }}
        </h2>
        <div>
          <ul class="space-y-2">
            <li
              v-for="page in showAllButton.documents
                ? results.documents.hits
                : results.documents.hits?.slice(0, 5)"
              :key="page.objectID"
              class="relative"
            >
              <template v-if="page.blueprint === 'file'">
                <FileItem :data="page" />
              </template>
            </li>
          </ul>
        </div>
        <AppButton
          v-if="results.documents.nbHits > 5 && !showAllButton.documents"
          is-secondary
          class="my-10 sm:my-16"
          @click="showAllButton.documents = true"
          >Alle Anzeigen</AppButton
        >
      </div>

      <!-- PAGES -->
      <div class="order-1 xl:order-2">
        <h2 class="mb-6 text-2xl font-medium sm:mb-8 sm:text-3xl">
          {{ t('search.pages_found', results.pages.nbHits || 0) }}
        </h2>
        <div>
          <ul class="space-y-8 sm:space-y-14">
            <li
              v-for="page in showAllButton.pages
                ? results.pages.hits
                : results.pages.hits?.slice(0, 5)"
              :key="page.objectID"
              class="relative"
            >
              <NuxtLink :to="page.url">
                <!-- TITLE -->
                <h3
                  class="mb-2 max-w-title text-md font-medium sm:mb-3 sm:text-lg"
                >
                  {{ page?.heading[0] || page.heading }}
                </h3>

                <!-- LINK -->
                <p class="mb-3 max-w-text text-blue-800">
                  {{ `${appUrl}${page.url}` }}
                </p>

                <!-- HIGHLIGHTED CONTENT -->
                <ul class="space-y-2 sm:space-y-3">
                  <li
                    v-for="(highlightResult, index) in extractMatchLevelFull(
                      page._highlightResult
                    )"
                    :key="index"
                  >
                    <p
                      class="highlight line-clamp-4 max-h-[5.5rem] max-w-text text-sm sm:line-clamp-none sm:max-h-none sm:text-base"
                      v-html="highlightResult.value"
                    />
                  </li>
                </ul>

                <!-- ARROW LINK -->
                <AppLink
                  show-link-arrow
                  :to="page.url"
                  class="mt-3 after:absolute after:inset-0 sm:mt-5"
                >
                  {{ $t('visit_page') }}
                </AppLink>
              </NuxtLink>
            </li>
          </ul>
        </div>
        <AppButton
          v-if="results.pages.nbHits > 5 && !showAllButton.pages"
          is-secondary
          class="my-10 sm:my-16"
          @click="showAllButton.pages = true"
          >Alle Anzeigen</AppButton
        >
      </div>
    </div>
  </Container>
</template>

<script setup>
// VARIABLES
const route = useRoute()
const params = useUrlSearchParams('history')
const { t } = useI18n()
const { setIsWhite } = useNavigationStore()

const showAllButton = ref({
  pages: false,
  documents: false,
})

const algoliaIndexPrefix = useRuntimeConfig().public.ALGOLIA_INDEX_PREFIX
if (!algoliaIndexPrefix) {
  throw new Error('ALGOLIA_INDEX_PREFIX not set in runtime config')
}

const appUrl = useRuntimeConfig().public.APP_URL

let results = ref({
  pages: [],
  documents: [],
})

// MOUNTED
onMounted(() => {
  setIsWhite(true)
})

// FUNCTIONS
async function search(value, waitForNextTick = false) {
  if (waitForNextTick) {
    await nextTick()
  }

  params['search'] = value
  if (value === '') {
    results.value = {
      pages: [],
      documents: [],
    }
    return
  }

  results.value.pages = await searchPages(value)
  results.value.documents = await searchDocuments(value)
}

async function searchPages(value) {
  const { data } = await useAsyncAlgoliaSearch({
    indexName: `${algoliaIndexPrefix}pages`,
    query: value,
  })
  return data
}

async function searchDocuments(value) {
  const { data } = await useAsyncAlgoliaSearch({
    indexName: `${algoliaIndexPrefix}downloads`,
    query: value,
  })
  return data
}

function extractMatchLevelFull(object, result = []) {
  for (let key in object) {
    if (object[key] && typeof object[key] === 'object') {
      if (
        object[key].hasOwnProperty('matchLevel') &&
        object[key].matchLevel !== 'none'
      ) {
        result.push(object[key])
      } else {
        extractMatchLevelFull(object[key], result)
      }
    }
  }
  return result
}

// INITIAL
// the initial fetch must be done in the setup function and can not be in an async function scope
if (route.query.search) {
  let payload = await useAsyncAlgoliaSearch({
    indexName: `${algoliaIndexPrefix}pages`,
    query: route.query.search,
  })
  results.value.pages = payload.data

  payload = await useAsyncAlgoliaSearch({
    indexName: `${algoliaIndexPrefix}downloads`,
    query: route.query.search,
  })
  results.value.documents = payload.data
}
</script>

<style lang="scss">
.highlight {
  em {
    @apply text-blue-dark-700 bg-blue-400 not-italic;
  }
}
</style>
